import { createSelector } from "reselect";
import { invert } from "ramda";
import accounting from "accounting";

// AccountType: FSA, MSA
// ItemType: benefits, wellness

const getAccountTypeForItemType = itemType => state => state.cart.accountDeduction[itemType];
const getAccountsInfo = state => state.cart.accounts;

const getAccountForAccountType = accountType =>
  createSelector(getAccountsInfo, accounts => accounts && accounts[accountType]);

const getAccountInfoForItemType = itemType => state =>
  getAccountForAccountType(getAccountTypeForItemType(itemType)(state))(state);

const getAccountBalanceForItemType = itemType =>
  createSelector(
    getAccountInfoForItemType(itemType),
    account => account && account.availableBalance
  );

// param1: { "benefits": "FSA", "wellness": "FSA" }
// output: { "FSA": ["benefits", "wellness"] }
const getItemTypesForAccountTypeMap = createSelector(
  state => state.cart.accountDeduction,
  state => invert(state)
);

// Returns ItemTypes which use the same AccountType as given ItemType
// param1: { "FSA": ["benefits", "wellness"] }
// param2: "FSA"
// output: ["benefits", "wellness"]
const getCartItemTypesForItemType = itemType =>
  createSelector(
    getItemTypesForAccountTypeMap,
    getAccountTypeForItemType(itemType),
    (accountTypeToItemTypesMap, accountType) => accountTypeToItemTypesMap[accountType]
  );

const getCartItemTypesForItemTypeMap = itemType =>
  createSelector(getCartItemTypesForItemType(itemType), itemTypes =>
    itemTypes.reduce((acc, type) => {
      acc[type] = 1;
      return acc;
    }, {})
  );

const getItemTypes = state => state.cart.itemTypes;
const getWellnessCartItemList = state => state.cart.wellnessItems.all;
const getWellnessCartEntities = state => state.cart.wellnessItems.byId;

const getSelectedPlan = plans => {
  const selectedPlan = plans.find(plan => plan.isSelected);
  if (!selectedPlan) {
    return null;
  }

  const {
    planId,
    price /*, relativePrice*/,
    type = "EE",
    options,
    eligibleMembers: { members = [] } = {},
    intervalOptions,
  } = selectedPlan;
  const selectionMode =
    members && members.length ? members[0].selectionMode : members ? members.selectionMode : "";
  const isIndividual = selectionMode && selectionMode.toLowerCase() === "individual";
  if (!options && (!selectionMode || !isIndividual)) {
    return { planId, type, price: accounting.unformat(price) };
  }

  if (isIndividual && intervalOptions && intervalOptions.length) {
    return { planId, type, price: accounting.unformat(price) };
  }

  // Update per member selection price
  let memberPrice = 0;

  if (members.length) {
    memberPrice = members
      .filter(member => member.isSelected && member.type !== "Employee" && member.price)
      .reduce((acc, curr) => acc + parseFloat(curr.price.numericValue), 0);
    const memberEmployee = members.find(member => member.type === "Employee");
    if (memberEmployee) {
      memberPrice += parseFloat(memberEmployee.price.numericValue);
    }
  }

  // Get price from actual price not from relative pricing
  const selectedOption = options ? options.find(option => option.isSelected) : null;
  if (!selectedOption) {
    return {
      planId,
      type,
      price: accounting.unformat(members.length && isIndividual ? memberPrice : price),
    };
  }

  // Get price from actual price not from relative pricing
  const { planId: optionPlanId, type: optionType, price: optionPrice } = selectedOption;

  return {
    planId: optionPlanId,
    type: optionType,
    price: accounting.unformat(members.length && isIndividual ? memberPrice : optionPrice),
  };
};

const getBenefitItemsById = benefitList => {
  return benefitList.reduce((obj, item) => {
    const { benefitId: id, plans } = item;
    const selectedPlan = getSelectedPlan(plans);
    if (selectedPlan) {
      const { planId, type: coverMemberType, price } = selectedPlan;
      obj[id] = {
        id,
        total: {
          id: planId,
          coverMemberType,
          price: {
            numericValue: price,
          },
        },
      };
    } else {
      obj[id] = null;
    }
    return obj;
  }, {});
};

const getBenefitCartItemList = state => state.cart.benefitItems.all;
// const getBenefitCartEntities = state => state.cart.benefitItems.byId;
const getBenefitCartEntities = state =>
  location.pathname.toLowerCase() === "/shop/enrolment"
    ? getBenefitItemsById(state.riskmedical.benefits)
    : state.cart.benefitItems.byId;

export const getWellnessAccountBalance = state => state.cart.available; //state.cart.wellnessBalance;
export const getBenefitsAccountBalance = state => state.cart.available; //state.cart.benefitsBalance;

const getWellnessCartTotal = createSelector(
  getWellnessCartItemList,
  getWellnessCartEntities,
  (ids, entities) =>
    ids.reduce(
      (acc, id) => acc + entities[id].quantity * accounting.unformat(entities[id].unitPrice),
      0
    )
);

const getBenefitCartTotal = createSelector(
  getBenefitCartItemList,
  getBenefitCartEntities,
  (ids, entities) =>
    ids.reduce((acc, id) => acc + (entities[id] && entities[id].total.price.numericValue) || 0, 0)
);

const getCartTotalsByItemType = createSelector(
  getBenefitCartTotal,
  getWellnessCartTotal,
  (benefits, wellness) => ({ benefits, wellness })
);

const getCartBreakdownForItemType = itemType =>
  createSelector(
    getItemTypes,
    getCartItemTypesForItemTypeMap(itemType),
    getCartTotalsByItemType,
    (itemTypes, itemTypesForItemTypeMap, totalsByItemType) =>
      itemTypes
        .map(
          type =>
            itemTypesForItemTypeMap[type.id] ? { ...type, total: totalsByItemType[type.id] } : null
        )
        .filter(x => x)
  );

const getCartSubTotalForItemType = itemType =>
  createSelector(
    getCartItemTypesForItemType(itemType),
    getCartTotalsByItemType,
    (itemTypes, totalsByItemType) =>
      itemTypes.reduce((acc, x) => acc + accounting.unformat(totalsByItemType[x]), 0)
  );

const getRemainingBalanceForItemType = itemType =>
  createSelector(
    getAccountBalanceForItemType(itemType),
    getCartSubTotalForItemType(itemType),
    (balance, subTotal) => balance - subTotal
  );

const getCartInfoForItemType = itemType =>
  createSelector(
    getAccountInfoForItemType(itemType),
    getAccountBalanceForItemType(itemType),
    getAccountTypeForItemType(itemType),
    getCartBreakdownForItemType(itemType),
    getCartSubTotalForItemType(itemType),
    getRemainingBalanceForItemType(itemType),
    (account, balance, accountType, totalBreakdown, subTotal, remaining) => ({
      account,
      balance,
      accountType,
      totalBreakdown,
      subTotal,
      remaining: Math.max(0, accounting.unformat(remaining)),
      overPurchase: -1 * Math.min(accounting.unformat(remaining), 0),
    })
  );

export const getCartInfo = createSelector(
  getCartInfoForItemType("benefits"),
  getCartInfoForItemType("wellness"),
  (benefits, wellness) => ({ benefits, wellness })
);



// WEBPACK FOOTER //
// ./components/employee/shop/cartSelectors.js